import { createBrowserRouter } from "react-router-dom";
import AuthGuard from "./AuthGuard";
import GuestGuard from "./GuestGuard";
import UserLayout from "@/layout/user";
import AdminLayout from "@/layout/admin";
import { routeConfig } from "./router";
import { GUEST_ONLY_PATHS } from "./landing";
import ErrorFallback from "@/layout/error/component/ErrorFallback";
import ErrorWrapper from "@/layout/error";
import PermissionGuard from "@/permissions/PermissionGuard";
import RouteGuard from "./RouteGuard";

const isGuestOnly = (path) => GUEST_ONLY_PATHS.includes(path);

/**
 * Public Routes
 * - Accessible without authentication
 *
 * Split in two: the screens that ask for credentials sit behind GuestGuard so
 * a signed-in user cannot land on them, while everything else public — the job
 * board, apply, the emailed recovery links — stays open to everyone, signed in
 * or not.
 */
const publicRoutes = routeConfig
  .filter((route) => route.isAuth === false && !isGuestOnly(route.path))
  .map(({ path, element }) => ({
    path,
    element,
  }));

const guestRoutes = routeConfig
  .filter((route) => route.isAuth === false && isGuestOnly(route.path))
  .map(({ path, element }) => ({
    path,
    element,
  }));

/**
 * Protected Routes
 * - Wrapped inside AuthGuard
 */
// `module` is destructured here on purpose. Every key not named below is
// dropped, so adding `module:` to a route object in `router.jsx` and stopping
// there gates nothing at all.
const protectedRoutes = routeConfig
  .filter((route) => route.isAuth === true)
  .map(({ path, element, permission, permissions, module }) => ({
    path,
    element: permission || permissions || module
      ? (
        <PermissionGuard
          permission={permission}
          permissions={permissions}
          module={module}
          redirectTo="/login"
        >
          {element}
        </PermissionGuard>
      )
      : element,
  }));

/**
 * App Router
 *
 * - Public routes are directly accessible
 * - Protected routes are nested under AuthGuard
 */
export const router = createBrowserRouter([
  {
    element: <ErrorWrapper />,
    children: [
      {
        element: <UserLayout />,
        children: publicRoutes,
      },
      {
        element: <GuestGuard />,
        children: [
          {
            element: <UserLayout />,
            children: guestRoutes,
          },
        ],
      },
      {
        element: <AuthGuard />,
        children: [
          {
            element: <RouteGuard />,
            children: [
              {
                element: <AdminLayout />,
                children: protectedRoutes,
              },
            ],
          },
        ],
      },
    ],
  },

  {
    path: "*",
    element: <ErrorFallback errorType={"NOT_FOUND"} />,
  },
]);
